import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useTheme } from '@/context/theme.context';
import { LuAreaChart, LuBarChart2, LuLineChart, LuArrowRight, LuMaximize2, LuX } from 'react-icons/lu';
import { SectionCard } from '@/components/organism/Cards/SectionCard';
import { IconBadge } from '@/components/atoms/IconBadge';
import { Text } from '@/components/atoms/AppText';
import SegmentedControl from '@/components/elements/SegmentedControl';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { APP_BG, APP_BORDER, APP_TEXT } from '@/constant/theme/colors.constant';
import { GtChart } from './GtChart';
import type { GtChartType } from './GtChart';

// ── Chart variant ──────────────────────────────────────────────────────────────────────
export type ChartVariant = 'area' | 'bar' | 'line';

const CHART_VARIANTS: Record<string, ChartVariant> = {
  mmsDevices: 'bar',
  mmsMarkets: 'bar',
  mmsProducts: 'bar',
  mmsSales: 'bar',
  alerts: 'bar',
};

/** Generate `count` progressive tints of a hex color by mixing toward white (0 % → 55 %). */
function hexTints(hex: string, count: number): string[] {
  const c = hex.replace('#', '');
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return Array.from({ length: Math.max(count, 1) }, (_, i) => {
    const mix = count === 1 ? 0 : (i / (count - 1)) * 0.55;
    const tr = Math.round(r + (255 - r) * mix);
    const tg = Math.round(g + (255 - g) * mix);
    const tb = Math.round(b + (255 - b) * mix);
    return `#${tr.toString(16).padStart(2, '0')}${tg.toString(16).padStart(2, '0')}${tb.toString(16).padStart(2, '0')}`;
  });
}

export interface DashboardChartData {
  series: { name: string; data: (number | string | null | undefined)[] }[];
  options: {
    xaxis?: { categories?: (string | number)[] };
    labels?: (string | number)[];
    [key: string]: unknown;
  };
}

interface DashboardChartProps {
  chartKey: string;
  title: string;
  subtitle?: string;
  /** Custom icon rendered in the header badge. Defaults to the active chart-type icon. */
  icon?: React.ReactNode;
  data: DashboardChartData;
  height?: number;
  forceVariant?: ChartVariant;
  className?: string;
  /** data-testid for automation */
  testId?: string;
  /** Optional "View all" navigation link shown in chart header */
  href?: string;

  // ── GtChart pass-through props ──────────────────────────────────────────────
  /** Show legend. Default: auto (shown when series > 1). */
  showLegend?: boolean;
  /** Legend placement. Default: 'bottom' */
  legendPosition?: 'bottom' | 'top' | 'right';
  /** Show horizontal grid lines. Default: true */
  showGrid?: boolean;
  /** Play entry animation. Default: true */
  animate?: boolean;
  /** Enable zoom/pan on the chart. Default: true */
  enableZoom?: boolean;
  /** Stack bar series. Default: false */
  stacked?: boolean;
  /** Line/area stroke width. Default: 2 */
  strokeWidth?: number;
  /** Area fill opacity at the top. Default: 0.10 */
  fillOpacity?: number;
  /** Bar top corner radius. Default: 4 */
  barRadius?: number;
  /** Fraction of band width used as gap between bar groups (0–1). Default: 0.28 */
  barGroupPadding?: number;
  /** Label shown when there is no data. Default: 'No data available' */
  noDataLabel?: string;
  /** Custom tooltip value formatter */
  formatTooltipValue?: (value: number, seriesName: string, catLabel: string) => string;
}

const VARIANT_ICONS: Record<ChartVariant, React.ElementType> = {
  area: LuAreaChart,
  bar: LuBarChart2,
  line: LuLineChart,
};

const VARIANT_LABELS: Record<ChartVariant, string> = {
  area: ORGANISM_PROPS.DASHBOARD_CHART.VARIANT_AREA,
  bar: ORGANISM_PROPS.DASHBOARD_CHART.VARIANT_BAR,
  line: ORGANISM_PROPS.DASHBOARD_CHART.VARIANT_LINE,
};

const VARIANTS: ChartVariant[] = ['bar', 'line', 'area'];
// const VARIANTS: ChartVariant[] = ['bar']; --- IGNORE ---

// Map chart variant to GtChart type
const toGtType = (v: ChartVariant): GtChartType => v as GtChartType;

const fmtTooltipVal = (v: number): string =>
  Number.isFinite(v) ? new Intl.NumberFormat().format(v) : '—';

const DashboardChart: React.FC<DashboardChartProps> = ({
  chartKey,
  title,
  subtitle,
  icon,
  data,
  height = 240,
  forceVariant,
  className = '',
  testId,
  href,
  // GtChart pass-through props with sensible defaults
  showLegend,
  legendPosition = 'bottom',
  showGrid = true,
  animate = true,
  enableZoom = true,
  stacked = false,
  strokeWidth = 2,
  fillOpacity = 0.10,
  barRadius = 4,
  barGroupPadding = 0.28,
  noDataLabel = 'No data available',
  formatTooltipValue,
}) => {
  const { isDark, colors } = useTheme();

  const defaultVariant = forceVariant ?? CHART_VARIANTS[chartKey] ?? 'bar';
  const [activeVariant, setActiveVariant] = useState<ChartVariant>(defaultVariant);
  const [isExpanded, setIsExpanded] = useState(false);

  // Lock body scroll when expanded + Escape to close
  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isExpanded]);

  useEffect(() => {
    if (!isExpanded) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isExpanded]);

  // ── Series colors ────────────────────────────────────────────────────────────
  // • alerts charts: fixed muted-red → warm-amber semantic palette (danger intent,
  //   should not follow the brand primary color).
  // • all other charts: progressive tints of the user's selected primary color.
  const seriesColors = useMemo(() => {
    const seriesList = data?.series ?? [];
    const count = Math.max(seriesList.length, 3);
    if (chartKey === 'alerts') {
      // muted danger palette: dusty red → warm terracotta → soft amber
      const alertPalette = ['#A04040', '#B06830', '#A08030'];
      return seriesList.map((_, i) => alertPalette[i % alertPalette.length]);
    }
    const tints = hexTints(colors.primary, count);
    return seriesList.map((_, i) => tints[i % tints.length]);
  }, [chartKey, data?.series, colors.primary]);

  // ── Category extraction ────────────────────────────────────────────────────
  const categories = useMemo(
    () => data?.options?.xaxis?.categories ?? data?.options?.labels ?? [],
    [data?.options],
  );

  // ── Series normalization ───────────────────────────────────────────────────
  const series = useMemo(
    () =>
      (data?.series ?? []).map((s) => ({
        name: s.name ?? '',
        data: (s.data ?? []).map((v: any) =>
          typeof v === 'number' ? v : typeof v === 'string' ? parseFloat(v) || 0 : 0,
        ),
      })),
    [data?.series],
  );

  const hasNoData =
    series.length === 0 || series.every((s) => s.data.every((v) => v === 0));

  const ActiveIcon = VARIANT_ICONS[activeVariant];
  const headerIcon = icon ?? <ActiveIcon size={14} />;

  // Chart-type switcher + optional View All link — passed as headerRight to SectionCard
  const chartSwitcher = (
    <div className="flex items-center gap-2">
      {href && !isExpanded && (
        <Link
          to={href}
          className="shrink-0 inline-flex items-center gap-0.5 text-[10px] font-semibold transition-opacity hover:opacity-60"
          style={{ color: colors.primary }}
        >
          View all <LuArrowRight size={9} />
        </Link>
      )}
      <SegmentedControl
        size="md"
        shape="square"
        variant="gradient"
        value={activeVariant}
        onChange={(v) => setActiveVariant(v as ChartVariant)}
        className="[&_button]:!py-1 [&_button]:!px-2"
        options={VARIANTS.map((v) => ({
          value: v,
          label: VARIANT_LABELS[v],
          icon: VARIANT_ICONS[v],
          iconOnly: true,
        }))}
      />
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded transition-opacity hover:opacity-60"
        style={{ color: colors.primary }}
        title="Expand fullscreen"
      >
        <LuMaximize2 size={13} />
      </button>
    </div>
  );

  const chartBody = (expandedHeight?: number) => (
    <div className="flex-1 pt-1 pb-0">
      {hasNoData ? (
        <div
          className="flex flex-col items-center justify-center gap-2"
          style={{ height: expandedHeight ?? height }}
        >
          <IconBadge
            icon={<LuBarChart2 size={18} style={{ opacity: 0.5 }} />}
            size="md"
          />
          <Text as="span" size="md" weight="medium" color="muted">
            {noDataLabel}
          </Text>
        </div>
      ) : (
        <GtChart
          key={`${chartKey}-${activeVariant}-${expandedHeight ?? height}`}
          type={toGtType(activeVariant)}
          series={series}
          categories={categories}
          height={expandedHeight ?? height}
          colors={seriesColors}
          isDark={isDark}
          animate={animate}
          showGrid={showGrid}
          showLegend={showLegend}
          legendPosition={legendPosition}
          enableZoom={enableZoom}
          stacked={stacked}
          strokeWidth={strokeWidth}
          fillOpacity={fillOpacity}
          barRadius={barRadius}
          barGroupPadding={barGroupPadding}
          noDataLabel={noDataLabel}
          formatTooltipValue={formatTooltipValue ?? ((v) => fmtTooltipVal(v))}
        />
      )}
    </div>
  );

  const fullscreenOverlay = isExpanded
    ? createPortal(
      <div className={`fixed inset-0 z-[9999] flex flex-col ${APP_BG.base}`}>
        {/* ── Title bar ── */}
        <div
          className={`flex-shrink-0 flex items-center justify-between gap-3 px-4 h-12 border-b border-gray-200 dark:border-white/[0.06] ${APP_BG.canvas}`}
        >
          {/* Left: icon + title */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="flex-shrink-0 text-gray-500 dark:text-gray-400">{headerIcon}</span>
              <Text as="span" size="sm" weight="bold" color="default" className="truncate">
                {title}
              </Text>
              {subtitle && (
                <Text as="span" size="xs" color="muted" className="truncate hidden sm:block">
                  {subtitle}
                </Text>
              )}
            </div>
          </div>
          {/* Right: variant switcher + X close */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <SegmentedControl
              size="md"
              shape="square"
              variant="gradient"
              value={activeVariant}
              onChange={(v) => setActiveVariant(v as ChartVariant)}
              className="[&_button]:!py-1 [&_button]:!px-2"
              options={VARIANTS.map((v) => ({
                value: v,
                label: VARIANT_LABELS[v],
                icon: VARIANT_ICONS[v],
                iconOnly: true,
              }))}
            />
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              className={`flex items-center justify-center w-7 h-7 rounded-md ${APP_TEXT.subtle} hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors`}
              title="Close (Esc)"
            >
              <LuX size={14} />
            </button>
          </div>
        </div>
        {/* ── Chart body ── */}
        <div className="flex-1 overflow-hidden flex flex-col px-5 pt-4 pb-10">
          {hasNoData ? (
            <div className="flex flex-col items-center justify-center gap-2 h-full">
              <IconBadge icon={<LuBarChart2 size={22} style={{ opacity: 0.5 }} />} size="md" />
              <Text as="span" size="md" weight="medium" color="muted">
                {noDataLabel}
              </Text>
            </div>
          ) : (
            <GtChart
              key={`${chartKey}-${activeVariant}-fs`}
              type={toGtType(activeVariant)}
              series={series}
              categories={categories}
              height={window.innerHeight - 48 - 56}
              colors={seriesColors}
              isDark={isDark}
              animate={animate}
              showGrid={showGrid}
              showLegend={showLegend}
              legendPosition={legendPosition}
              enableZoom={enableZoom}
              stacked={stacked}
              strokeWidth={strokeWidth}
              fillOpacity={fillOpacity}
              barRadius={barRadius}
              barGroupPadding={barGroupPadding}
              noDataLabel={noDataLabel}
              formatTooltipValue={formatTooltipValue ?? ((v) => fmtTooltipVal(v))}
            />
          )}
        </div>
      </div>,
      document.body,
    )
    : null;

  return (
    <>
      <SectionCard
        testId={testId}
        icon={headerIcon}
        title={title}
        subtitle={subtitle}
        headerRight={chartSwitcher}
        // iconVariant="light"
        headerClassName={APP_BG.canvas}
        bodyBg={APP_BG.base}
        bodyClassName=""
        className={className}
      >
        {/* ── Chart area ── */}
        {chartBody()}
      </SectionCard>
      {fullscreenOverlay}
    </>
  );
};

export default React.memo(DashboardChart);
